/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import Script from 'next/script';
import { useBfcacheSupport, polyfillWebLocksForBfcache } from '@/hooks/useBfcacheSupport';
import Link from 'next/link';
import { createClient } from '@/app/[locale]/utils/supabase/client';

// Define types for Truevo response objects
export interface TruevoResponse {
  status: string;
  data?: Record<string, unknown>;
  error?: {
    message: string;
    code: string;
  };
  transactionId?: string;
  transactionStatus?: string;
  responseCode?: string;
  merchantReference?: string;
  paymentStatus?: string;
}

interface TruevoEvent {
  detail: TruevoResponse;
}

// Define the interfaces for Truevo checkout with proper types
interface TruevoCheckoutInstance {
  addEventListener: (event: string, callback: (event: TruevoEvent) => void) => void;
  removeEventListener: (event: string, callback: (event: TruevoEvent) => void) => void;
  // Add alternative event properties
  onloadCompleted?: (event: TruevoEvent) => void;
  onloadFailed?: (event: TruevoEvent) => void;
  ontransactionCompleted?: (event: TruevoEvent) => void;
  ontransactionDeclined?: (event: TruevoEvent) => void;
  ontransactionFailed?: (event: TruevoEvent) => void;
}

interface TruevoCheckout {
  initialize: (configuration: Record<string, unknown>) => void;
  mount: (selector: string) => TruevoCheckoutInstance;
}

// Define OrderData interface to replace any
interface OrderData {
  items: Array<{
    id: string;
    quantity: number;
    product: Record<string, unknown>;
    [key: string]: unknown;
  }>;
  customerInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    shippingAddress?: Record<string, string>;
    billingAddress?: Record<string, string>;
  };
  shippingMethod?: {
    id: string;
    name: string;
    price: number;
  };
  [key: string]: unknown;
}

interface TruevoCheckoutProps {
  amount: number;
  currencyCode?: string;
  customerInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    address?: {
      street1?: string;
      street2?: string;
      city?: string;
      state?: string;
      postCodeZip?: string;
      countryAlphaCode?: string;
    };
  };
  // Make orderData optional since order creation is handled by parent
  orderData?: OrderData;
  onTransactionCompleted?: (response: TruevoResponse) => void;
  onTransactionDeclined?: (response: TruevoResponse) => void;
  onTransactionFailed?: (response: TruevoResponse) => void;
  onLoadCompleted?: (response: TruevoResponse) => void;
  onLoadFailed?: (response: TruevoResponse) => void;
}

declare global {
  interface Window {
    TruevoCheckout?: TruevoCheckout;
  }
}

// WebLocks polyfill specifically for Truevo
const WebLocksPolyfillForTruevo = () => (
  <script 
    id="weblocks-truevo-fix" 
    dangerouslySetInnerHTML={{
      __html: `
      (function() {
        try {
          // Early detection and polyfill for WebLocks API to prevent bfcache issues with Truevo
          if (typeof navigator !== 'undefined' && 'locks' in navigator) {
            
            // Store original implementation
            var originalLocks = navigator.locks;
            
            // Replace with dummy implementation that doesn't block bfcache
            Object.defineProperty(navigator, 'locks', {
              value: {
                request: function(name, options, callback) {
                  
                  // Just execute the callback immediately without actual locking
                  if (typeof callback === 'function') {
                    return Promise.resolve(callback({ name: name }));
                  } else if (typeof options === 'function') {
                    return Promise.resolve(options({ name: name }));
                  }
                  return Promise.resolve();
                },
                query: function() {
                  return Promise.resolve({ held: [], pending: [] });
                }
              },
              configurable: true
            });
          }
        } catch(e) {
          console.error('[Truevo] Error applying WebLocks polyfill:', e);
        }
      })();
      `
    }}
  />
);

export default function TruevoCheckout({
  amount,
  currencyCode = 'EUR',
  customerInfo = {},
  onTransactionCompleted,
  onTransactionDeclined,
  onTransactionFailed,
  onLoadCompleted,
  onLoadFailed,
}: TruevoCheckoutProps) {
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const checkoutInstanceRef = useRef<TruevoCheckout | null>(null);
  const mountedRef = useRef(false);
  // Add a flag to track if payment was successful
  const paymentSuccessfulRef = useRef(false);
  // Add state for credentials
  const [credentials, setCredentials] = useState<{merchantId: string; terminalId: string} | null>(null);
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
  // Add state for user verification
  const [isVerifyingUser, setIsVerifyingUser] = useState(false);
  // Add state for retry tracking
  const [credentialRetries, setCredentialRetries] = useState(0);
  const maxRetries = 3; // Maximum number of retries
  const [retryDelay, setRetryDelay] = useState(1000); // Initial retry delay in ms
  
  const mountRef = useRef<string | null>(null);
  
  // Add semaphore to prevent multiple concurrent credential fetches
  const isFetchingRef = useRef(false);
  // Add timestamp of last attempt to further prevent rapid requests
  const lastAttemptRef = useRef(0);
  
  // Store references to event handlers with proper types
  const eventHandlersRef = useRef<{
    loadCompleted: ((res: TruevoEvent) => void) | null;
    loadFailed: ((res: TruevoEvent) => void) | null;
    transactionCompleted: ((res: TruevoEvent) => void) | null;
    transactionDeclined: ((res: TruevoEvent) => void) | null;
    transactionFailed: ((res: TruevoEvent) => void) | null;
  }>({
    loadCompleted: null,
    loadFailed: null,
    transactionCompleted: null,
    transactionDeclined: null,
    transactionFailed: null
  });
  
  // Use our bfcache support hook to properly handle restoring from bfcache
  useBfcacheSupport(() => {
    // Don't do anything if payment was already successful
    if (paymentSuccessfulRef.current) return;

    // When page is restored from bfcache, we need to reinitialize
    
    // If we have a container and Truevo is loaded
    if (window.TruevoCheckout && containerRef.current) {
      // Completely reinitialize instead of just remounting, to ensure all event handlers are properly reattached
      
      // Use a delay to ensure the DOM is fully restored
      setTimeout(() => {
        // No need for try/catch if we're not using the errors
        cleanupCheckout();
        
        // Try to initialize, but don't worry if it fails
        initializeCheckout().catch(() => {
          // Last resort: If reinitializing fails, try a basic remount
          if (window.TruevoCheckout && containerRef.current && mountRef.current) {
            containerRef.current.innerHTML = '';
            try {
              window.TruevoCheckout.mount(mountRef.current);
            } catch {
              // Silent error handling - nothing else we can do
            }
          }
        });
      }, 350); // Slightly longer delay to ensure full page restoration
    }
  });

  // Check if user already exists with the given email
  const verifyUserDoesNotExist = useCallback(async () => {
    if (!customerInfo.email) {
      return true; // Can't verify without email
    }

    // Get current user from Supabase
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    // If logged in and emails match, allow checkout
    if (user && user.email && user.email.toLowerCase() === customerInfo.email.toLowerCase()) {
      return true;
    }

    try {
      setIsVerifyingUser(true);
      setPaymentError(null);

      const response = await fetch('/api/users/check-exists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
        body: JSON.stringify({ email: customerInfo.email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to verify user status');
      }

      const { exists } = await response.json();

      if (exists) {
        setPaymentError('This email address already has an account. Please log in before completing your purchase.');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error verifying user status:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to verify account status. Please try again.';

      setPaymentError(errorMessage);
      return false;
    } finally {
      setIsVerifyingUser(false);
    }
  }, [customerInfo.email]);
  
  // Fetch Truevo credentials from server
  const fetchCredentials = useCallback(async () => {
    // Guard against concurrent requests
    if (isFetchingRef.current) {
      return null;
    }
    
    // Prevent too frequent retries (at least 500ms between attempts)
    const now = Date.now();
    if (now - lastAttemptRef.current < 500) {
      return null;
    }
    lastAttemptRef.current = now;
    
    // Don't retry more than maxRetries times
    if (credentialRetries >= maxRetries) {
      console.error(`[Truevo] Maximum credential fetch retries (${maxRetries}) reached, stopping attempts`);
      setPaymentError('Unable to load payment credentials after multiple attempts. Please refresh the page or try again later.');
      return null;
    }

    try {
      isFetchingRef.current = true;
      setIsLoadingCredentials(true);
      setPaymentError(null); // Clear any previous errors when we start fetching
      
      const response = await fetch('/api/payments/truevo/credentials', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add cache control to prevent caching issues
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          // Add custom header to help server identify checkout context
          'X-Context': 'checkout',
        },
        // Important: Add cache: 'no-store' to prevent caching issues
        cache: 'no-store',
      });
      
      // Log response status for debugging
      
      if (!response.ok) {
        // Calculate exponential backoff for next retry
        const nextRetryDelay = Math.min(retryDelay * 2, 10000); // Cap at 10 seconds
        setRetryDelay(nextRetryDelay);
        
        // Increment retry counter
        setCredentialRetries(prev => prev + 1);
        
        let errorMessage = 'Failed to fetch payment credentials';
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          console.error('[Truevo] API error details:', errorData);
        } catch (parseError) {
          console.error('[Truevo] Error parsing API error response:', parseError);
        }
        
        // If unauthorized, show specific message
        if (response.status === 401) {
          errorMessage = 'Authentication required. Please sign in to continue with payment.';
        }
        
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Validate that we received the expected data
      if (!data.merchantId || !data.terminalId) {
        throw new Error('Incomplete payment credentials received from server');
      }
      
      // Reset retry counter and delay on success
      setCredentialRetries(0);
      setRetryDelay(1000);
      
      setCredentials({
        merchantId: data.merchantId,
        terminalId: data.terminalId,
      });
      return data;
    } catch (error) {
      console.error('[Truevo] Error fetching Truevo credentials:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to load payment credentials. Please try again.';
      
      setPaymentError(errorMessage);
      
      // If we're retrying, set a timeout to try again with exponential backoff
      // BUT only if we haven't reached max retries
      if (credentialRetries < maxRetries) {
        const timerId = setTimeout(() => {
          if (!credentials && !isLoadingCredentials) {
            fetchCredentials();
          }
        }, retryDelay);
        
        // Safety cleanup to prevent memory leaks
        return () => clearTimeout(timerId);
      }
      
      return null;
    } finally {
      setIsLoadingCredentials(false);
      isFetchingRef.current = false;
    }
  }, [credentialRetries, retryDelay, credentials, isLoadingCredentials]);

  // Manual retry function for the user
  const handleRetryFetch = useCallback(() => {
    // Reset retry state
    setCredentialRetries(0);
    setRetryDelay(1000);
    // Clear error
    setPaymentError(null);
    // Try again
    fetchCredentials();
  }, [fetchCredentials]);

  // Helper to cleanup existing checkout instance
  const cleanupCheckout = useCallback(() => {
    // Don't cleanup if payment was successful
    if (paymentSuccessfulRef.current) return;

    if (checkoutInstanceRef.current) {
      try {
        // Use type assertion to avoid TypeScript errors
        const instance = checkoutInstanceRef.current as any;
        
        // Clear both addEventListener-style and direct property event handlers
        if (typeof instance.removeEventListener === 'function') {
          // Try to remove event listeners the proper way
          const handlers = eventHandlersRef.current;
          
          if (handlers.loadCompleted) {
            try { instance.removeEventListener('loadCompleted', handlers.loadCompleted); } 
            catch { /* Silent fail */ }
          }
          
          if (handlers.loadFailed) {
            try { instance.removeEventListener('loadFailed', handlers.loadFailed); } 
            catch { /* Silent fail */ }
          }
          
          if (handlers.transactionCompleted) {
            try { instance.removeEventListener('transactionCompleted', handlers.transactionCompleted); } 
            catch { /* Silent fail */ }
          }
          
          if (handlers.transactionDeclined) {
            try { instance.removeEventListener('transactionDeclined', handlers.transactionDeclined); } 
            catch { /* Silent fail */ }
          }
          
          if (handlers.transactionFailed) {
            try { instance.removeEventListener('transactionFailed', handlers.transactionFailed); } 
            catch { /* Silent fail */ }
          }
        }
        
        // Also clear direct property assignments (belt and suspenders approach)
        instance.onloadCompleted = undefined;
        instance.onloadFailed = undefined;
        instance.ontransactionCompleted = undefined;
        instance.ontransactionDeclined = undefined;
        instance.ontransactionFailed = undefined;
        
        // Try to clear internal event registry if it exists
        if (instance._events) {
          try {
            instance._events.loadCompleted = [];
            instance._events.loadFailed = [];
            instance._events.transactionCompleted = [];
            instance._events.transactionDeclined = [];
            instance._events.transactionFailed = [];
          } catch {
            // Silent fail
          }
        }
        
      } catch {
        // Silent fail
      }
      
      // Reset the reference
      checkoutInstanceRef.current = null;
    }
    
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
    
    // Reset the event handlers refs
    eventHandlersRef.current = {
      loadCompleted: null,
      loadFailed: null,
      transactionCompleted: null,
      transactionDeclined: null,
      transactionFailed: null
    };
  }, []);

  // Add this function to safely attach event handlers with multiple methods to ensure success
  const safelyAttachEventHandler = useCallback((eventName: string, handler: (event: TruevoEvent) => void) => {
    if (!checkoutInstanceRef.current) return;
    
    // Always store the handler in our ref for potential cleanup
    switch (eventName) {
      case 'loadCompleted':
        eventHandlersRef.current.loadCompleted = handler;
        break;
      case 'loadFailed':
        eventHandlersRef.current.loadFailed = handler;
        break;
      case 'transactionCompleted':
        eventHandlersRef.current.transactionCompleted = handler;
        break;
      case 'transactionDeclined':
        eventHandlersRef.current.transactionDeclined = handler;
        break;
      case 'transactionFailed':
        eventHandlersRef.current.transactionFailed = handler;
        break;
    }
    
    // Get instance and cast to any to bypass TypeScript restrictions
    const instance = checkoutInstanceRef.current as any;
    
    // Method 1: Try addEventListener if available
    try {
      if (typeof instance.addEventListener === 'function') {
        instance.addEventListener(eventName, handler);
      }
    } catch {
      // Silent fail
    }
    
    // Method 2: Always set direct property (most reliable method)
    try {
      const propertyName = `on${eventName}`;
      instance[propertyName] = handler;
    } catch {
      // Silent fail
    }
    
    // Method 3: Try to modify internal events registry if it exists
    try {
      if (instance._events && typeof instance._events === 'object') {
        instance._events[eventName] = instance._events[eventName] || [];
        instance._events[eventName].push(handler);
      }
    } catch {
      // Silent fail
    }
  }, []);

  const initializeCheckout = useCallback(async () => {
    // Don't initialize if payment was successful
    if (paymentSuccessfulRef.current) return;
    
    // Clean up any existing instance first
    cleanupCheckout();
    
    // If TruevoCheckout is not loaded or container is not available, exit
    if (!window.TruevoCheckout || !containerRef.current) return;
    
    // Reset error state when initializing
    setPaymentError(null);

    try {
      // First verify that this email doesn't already have an account
      if (customerInfo.email) {
        const userVerificationPassed = await verifyUserDoesNotExist();
        if (!userVerificationPassed) {
          throw new Error('This email address already has an account. Please log in before completing your purchase.');
        }
      }
      
      // Ensure we have credentials
      let truevoCredentials = credentials;
      if (!truevoCredentials) {
        truevoCredentials = await fetchCredentials();
        if (!truevoCredentials) {
          throw new Error('Unable to load payment credentials');
        }
      }
      
      const merchantReference = `Order-${Date.now()}`;
      
      // Format amount to exactly 2 decimal places
      const formattedAmount = Number(amount.toFixed(2));
      
      const configuration = {
        transactionType: 'sale',
        widgetConfiguration: {
            language: 'EN',
            showTruevo: true,
            iframeStyles: {
                theme: 'classic',
                isDarkTheme: true,
                style: 'body {background: none;} #truevo_form_container>.truevo-card-result-div>.truevo-card-result-div-title {font-size: 20px!important;}'
            },
        },
        transaction: {
            merchantReference,
            paymentReference: merchantReference,
            amount: {
                totalAmount: formattedAmount,
                currencyAlphaCode: currencyCode,
                details: {
                    paymentAmount: formattedAmount,
                    gratuityAmount: 0
                }
            },
        },
        merchant: {
            descriptor: 'THCA Space',
            mid: truevoCredentials.merchantId,
            tid: truevoCredentials.terminalId,
            redirectUrl: window.location.origin,
        },
        customer: {
            firstName: customerInfo.firstName || '',
            lastName: customerInfo.lastName || '',
            contact: {
              email: customerInfo.email || '',
              mobile: customerInfo.phone || '',
              phone: customerInfo.phone || ''
            },
            billingAddress: {
              street1: customerInfo.address?.street1 || '',
              street2: customerInfo.address?.street2 || '',
              city: customerInfo.address?.city || '',
              state: customerInfo.address?.state || '',
              postCodeZip: customerInfo.address?.postCodeZip || '',
              countryAlphaCode: customerInfo.address?.countryAlphaCode || 'GB'
            }
        },
        additionalData: [
        ]
      };

      
      // Initialize the Truevo checkout
      window.TruevoCheckout.initialize(configuration);
      
      // Clear container and mount checkout
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
        
        try {
          // Mount checkout and store instance
          const instance = window.TruevoCheckout.mount('#truevo-container') as any;
          // Store instance for future use
          checkoutInstanceRef.current = instance || window.TruevoCheckout;
          // Store mount selector for bfcache restoration
          mountRef.current = '#truevo-container';
          
          // Set up critical transaction handlers with brief delay to ensure they register
          setTimeout(() => {
            // Transaction completed - most important
            if (onTransactionCompleted) {
              const handler = (event: TruevoEvent) => {
                // Set the payment successful flag to prevent reinitialization
                paymentSuccessfulRef.current = true;
                
                // Extract necessary payment details from the response
                const response = event.detail;
                const transactionData = {
                  transactionId: response.transactionId || (response.data?.transactionInfo as any)?.transactionId || '',
                  paymentReference: response.merchantReference || (response.data?.transactionInfo as any)?.merchantReference || '',
                  paymentStatus: 'complete'
                };
                
                // Pass the structured data to the parent component
                onTransactionCompleted({
                  ...response,
                  // Ensure these fields are available at the top level
                  transactionId: transactionData.transactionId,
                  merchantReference: transactionData.paymentReference,
                  paymentStatus: transactionData.paymentStatus
                });
              };
              safelyAttachEventHandler('transactionCompleted', handler);
            }

            // Transaction declined
            if (onTransactionDeclined) {
              const handler = (event: TruevoEvent) => {
                setPaymentError(`Payment declined: ${event.detail.error?.message || 'Unknown error'}`);
                onTransactionDeclined(event.detail);
              };
              safelyAttachEventHandler('transactionDeclined', handler);
            }

            // Transaction failed
            if (onTransactionFailed) {
              const handler = (event: TruevoEvent) => {
                setPaymentError(`Payment failed: ${event.detail.error?.message || 'Unknown error'}`);
                onTransactionFailed(event.detail);
              };
              safelyAttachEventHandler('transactionFailed', handler);
            }

            // Also set up other handlers
            if (onLoadCompleted) {
              safelyAttachEventHandler('loadCompleted', (event) => {
                onLoadCompleted(event.detail);
              });
            }

            if (onLoadFailed) {
              safelyAttachEventHandler('loadFailed', (event) => {
                setPaymentError(event.detail.error?.message || 'Failed to load payment form');
                onLoadFailed(event.detail);
              });
            }
            
          }, 200);
          
        } catch (mountError) {
          console.error('[Truevo] Error during checkout mount:', mountError);
          setPaymentError('Failed to display payment form. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error initializing Truevo checkout:', error);
      setPaymentError(error instanceof Error ? error.message : 'Failed to initialize payment form. Please try again.');
    }
  }, [amount, currencyCode, customerInfo, onLoadCompleted, onLoadFailed, onTransactionCompleted, onTransactionDeclined, onTransactionFailed, cleanupCheckout, fetchCredentials, credentials, verifyUserDoesNotExist, safelyAttachEventHandler]);

  // Helper for script loading - MOVED AFTER initializeCheckout
  const handleScriptLoad = useCallback(() => {
    if (window.TruevoCheckout && !mountedRef.current) {
      mountedRef.current = true;
      // Initialize with delay to ensure all scripts are loaded
      setTimeout(() => {
        initializeCheckout();
      }, 500);
    }
  }, [initializeCheckout]);

  // Initialize checkout when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (window.TruevoCheckout && containerRef.current && !mountedRef.current) {
        mountedRef.current = true;
        initializeCheckout();
      }
    }, 800);
    
    return () => clearTimeout(timer);
  }, [initializeCheckout]);

  // Fetch credentials when component mounts - MODIFIED to only try once on mount
  useEffect(() => {
    let isMounted = true;
    
    // Only attempt to fetch credentials once on mount if not already loading or fetched
    if (!credentials && !isLoadingCredentials && credentialRetries === 0 && isMounted) {
      fetchCredentials();
    }
    
    return () => {
      isMounted = false; // Prevent state updates after unmount
    };
  }, [credentials, isLoadingCredentials, fetchCredentials, credentialRetries]);

  // Reset the mounted flag when the component remounts (via key prop)
  useEffect(() => {
    mountedRef.current = false;
    
    // Return cleanup function
    return () => {
      cleanupCheckout();
      mountedRef.current = false;
    };
  }, [cleanupCheckout]);

  // Apply WebLocks polyfill when component mounts
  useEffect(() => {
    // Apply the polyfill programmatically
    polyfillWebLocksForBfcache();
  }, []);

  return (
    <div className="truevo-checkout-wrapper w-full h-full">
      <div className="bg-surface rounded-lg shadow-md shadow-border/20 p-4">
        {/* WebLocks polyfill */}
        <WebLocksPolyfillForTruevo />
        
        {/* Scripts */}
        <Script
          src="https://docs.truevo.com/wp-content/cache/min/1/widget/merchant.minified.js?ver=1692209093"
          strategy="afterInteractive"
          onLoad={handleScriptLoad}
        />
        <Script
          src="https://docs.truevo.com/wp-content/cache/min/1/jsapi/v1/apple-pay-sdk.js?ver=1692209094"
          strategy="afterInteractive"
        />
        <Script
          src="https://docs.truevo.com/wp-content/cache/min/1/npm/crypto-js/crypto-js.js?ver=1692209094"
          strategy="afterInteractive"
        />

        {/* Payment summary */}
        <div className="payment-summary mb-3 p-2 bg-surface-hover rounded-md shadow-sm">
          <div className="flex justify-between items-center text-text-main text-sm">
            <span>Total:</span>
            <span className="font-bold">{new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: currencyCode
            }).format(amount)}</span>
          </div>
        </div>
        
        {/* Loading indicator */}
        {(isLoadingCredentials || isVerifyingUser) && (
          <div className="flex justify-center items-center p-4 min-h-[200px]">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              <p className="mt-2 text-text-main text-sm">
                {isVerifyingUser ? 'Verifying account...' : 'Loading payment form...'}
              </p>
            </div>
          </div>
        )}
        
        {/* Payment form container */}
        <div id="truevo-container" ref={containerRef} className={`w-full min-h-[350px] rounded-md overflow-hidden ${isLoadingCredentials || isVerifyingUser ? 'hidden' : 'block'}`} />
        
        {/* Credential fetch error */}
        {paymentError && paymentError.includes('credentials') && !isLoadingCredentials && (
          <div className="mt-3 p-3 bg-status-error/20 rounded-md shadow-sm text-text-main">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-status-error" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <h3 className="text-xs font-medium text-status-error">Configuration Error</h3>
                <div className="mt-1 text-xs text-text-main">
                  {paymentError}
                  {credentialRetries >= maxRetries && (
                    <div className="mt-1 text-status-warning">
                      Maximum retry attempts reached. Please try again manually.
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={handleRetryFetch}
                    className="inline-flex items-center px-2 py-1 text-xs leading-4 font-medium rounded-md shadow-sm text-text-main bg-surface-hover hover:bg-surface-active focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    Retry Loading Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Account exists error */}
        {paymentError && paymentError.includes('already has an account') && (
          <div className="mt-3 p-3 bg-status-warning/20 rounded-md shadow-sm text-text-main">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-status-warning" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <h3 className="text-xs font-medium text-status-warning">Account Required</h3>
                <div className="mt-1 text-xs text-text-main">{paymentError}</div>
                <div className="mt-2 flex flex-col sm:flex-row gap-2">
                  <Link
                    href="/login"
                    className="inline-flex items-center justify-center px-3 py-1.5 text-xs leading-4 font-medium rounded-md shadow-sm text-static-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    Sign In
                  </Link>
                  <button
                    type="button"
                    onClick={() => window.location.href = '/login?createAccount=true'}
                    className="inline-flex items-center justify-center px-3 py-1.5 text-xs leading-4 font-medium rounded-md shadow-sm text-text-main bg-surface-hover hover:bg-surface-active focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    Create New Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Other payment errors */}
        {paymentError && !paymentError.includes('credentials') && !paymentError.includes('already has an account') && (
          <div className="mt-3 p-3 bg-status-error/20 rounded-md shadow-sm text-text-main">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-status-error" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <h3 className="text-xs font-medium text-status-error">Payment Error</h3>
                <div className="mt-1 text-xs text-text-main">{paymentError}</div>
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={initializeCheckout}
                    className="inline-flex items-center px-2 py-1 text-xs leading-4 font-medium rounded-md shadow-sm text-text-main bg-surface-hover hover:bg-surface-active focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    Retry Payment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Secure payment message */}
        <div className="mt-3 text-xs text-gray-400 flex items-center justify-center">
          <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Secure payment
        </div>
      </div>
    </div>
  );
}

/* 
 * Truevo Test Card Information:
 * ==============================================
 * Card Numbers:
 * 5104150000008851
 * 5315480000007826
 * 4443550000000514
 * 4503030000007410
 * Expiry Date: Any future date (MM/YY)
 * CVV: Any 3 digits
 */ 